import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import CONFIG from '../../config';
import EstadisticaAsistencia from '../estudiantes/EstadisticaAsistencia';
import GraficoAsistencia from '../estudiantes/GraficoAsistencia';
import CalendarioAsistencia from '../estudiantes/CalendarioAsistencia';
import PerfilLogo from '../usuarios/PerfilLogo';
import Espera from '../Espera';
import './css/CargarAsistencia.css';

const URL_REPORTES = `${CONFIG.API_URL}/operarReportes.php`;
const URL_ASISTENCIA = `${CONFIG.API_URL}/operarAsistencia.php`;

function CargarAsistencia({ acceder, rol, configuracion }) {
    const navigate = useNavigate();

    // Fecha actual por defecto en YYYY-MM-DD
    const [fechaHoy] = useState(() => new Date().toLocaleDateString('sv-SE'));
    const [fechaSeleccionada, setFechaSeleccionada] = useState(fechaHoy);

    // Filtros en cascada
    const [combinaciones, setCombinaciones] = useState([]);
    const [filtroCiclo, setFiltroCiclo] = useState('');
    const [filtroNivel, setFiltroNivel] = useState('');
    const [filtroOrden, setFiltroOrden] = useState('');
    const [filtroSeccion, setFiltroSeccion] = useState('');

    // Opciones dinámicas para selects
    const [opcionesCiclos, setOpcionesCiclos] = useState([]);
    const [opcionesNiveles, setOpcionesNiveles] = useState([]);
    const [opcionesOrdenes, setOpcionesOrdenes] = useState([]);
    const [opcionesSecciones, setOpcionesSecciones] = useState([]);

    // Datos de la asistencia y estudiantes
    const [estudiantes, setEstudiantes] = useState([]);
    const [asistencia, setAsistencia] = useState([]);
    const [asistenciaAnual, setAsistenciaAnual] = useState([]);
    const [fechasConAsistencia, setFechasConAsistencia] = useState([]);
    const [formValuesAsistencia, setFormValuesAsistencia] = useState({});

    // Tipo de Turno General: 0 = Turno Habitual (id_curso=0), -1 = Contraturno (id_curso=-1)
    const [tipoTurno, setTipoTurno] = useState(0);

    // Estados de UI
    const [loadingFiltros, setLoadingFiltros] = useState(true);
    const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);
    const [verCalendario, setVerCalendario] = useState(false);
    const [guardando, setGuardando] = useState(false);

    const loggeduserId = localStorage.getItem('loggedUserId');
    const rolNum = Number(rol);

    // Verificación de acceso para rol 10 (Preceptor) y roles directivos (1, 2, 3, 4)
    useEffect(() => {
        if (!acceder || (rolNum >= 5 && rolNum !== 10)) {
            navigate('/');
            return;
        }
        cargarFiltros();
    }, [acceder, rolNum, navigate]);

    // Cargar filtros iniciales
    const cargarFiltros = async () => {
        setLoadingFiltros(true);
        try {
            const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
            const res = await axios.post(URL_REPORTES, {
                modo: 'filtrosOpciones',
                id_nivel: loggeduserNivelId
            });
            if (!res.data.error) {
                const comb = res.data.combinaciones || [];
                setCombinaciones(comb);
                const ciclosUnicos = [...new Set(comb.map(c => c.ciclo))].sort((a, b) => b.localeCompare(a));
                setOpcionesCiclos(ciclosUnicos);
                if (ciclosUnicos.length > 0) {
                    setFiltroCiclo(ciclosUnicos[0]);
                }
            }
        } catch (e) {
            console.error("Error al cargar filtros:", e);
        } finally {
            setLoadingFiltros(false);
        }
    };

    // Efecto cascada de los filtros
    useEffect(() => {
        if (combinaciones.length === 0) return;

        let combisNivel = combinaciones;
        if (filtroCiclo) combisNivel = combisNivel.filter(c => String(c.ciclo) === String(filtroCiclo));

        const nivelesMap = new Map();
        combisNivel.forEach(c => nivelesMap.set(c.nivel_id, c.nivel_nombre));
        const nivelesArr = Array.from(nivelesMap, ([id, nombre]) => ({ id, nombre }));
        setOpcionesNiveles(nivelesArr);

        let combisOrden = combisNivel;
        if (filtroNivel) combisOrden = combisOrden.filter(c => String(c.nivel_id) === String(filtroNivel));

        const ordenesUnicos = [...new Set(combisOrden.map(c => c.orden))].sort((a, b) => a - b);
        setOpcionesOrdenes(ordenesUnicos);

        let combisSeccion = combisOrden;
        if (filtroOrden) combisSeccion = combisSeccion.filter(c => String(c.orden) === String(filtroOrden));

        const seccionesUnicas = [...new Set(combisSeccion.map(c => c.division))].sort((a, b) => a.localeCompare(b));
        setOpcionesSecciones(seccionesUnicas);
    }, [combinaciones, filtroCiclo, filtroNivel, filtroOrden]);

    // Cargar estudiantes y asistencia general (curso_grupo = 0 o -1)
    useEffect(() => {
        if (!filtroCiclo || !filtroNivel || !filtroOrden || !filtroSeccion) {
            setEstudiantes([]);
            setAsistencia([]);
            setAsistenciaAnual([]);
            setFechasConAsistencia([]);
            setFormValuesAsistencia({});
            return;
        }

        cargarEstudiantesYAsistencia();
    }, [filtroCiclo, filtroNivel, filtroOrden, filtroSeccion, fechaSeleccionada, tipoTurno]);

    const cargarEstudiantesYAsistencia = async () => {
        setLoadingEstudiantes(true);
        try {
            const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
            
            // 1. Obtener lista de estudiantes del curso/división seleccionada sin dividir por espacio
            const resEst = await axios.post(URL_REPORTES, {
                modo: 'estudiantesPorCurso',
                id_nivel: loggeduserNivelId,
                ciclo: filtroCiclo,
                nivel: filtroNivel,
                orden: filtroOrden,
                seccion: filtroSeccion,
                espacio: 'general'
            });

            if (resEst.data && !resEst.data.error && resEst.data.datos) {
                // Filtrar duplicados de estudiantes
                const unicosMap = new Map();
                resEst.data.datos.forEach(d => {
                    const estId = d.estudiante_id || d.id;
                    if (estId && !unicosMap.has(estId)) {
                        unicosMap.set(estId, {
                            id: estId,
                            nombre: d.nombre,
                            apellido: d.apellido,
                            documento: d.documento
                        });
                    }
                });
                const listaEst = Array.from(unicosMap.values()).sort((a, b) => a.apellido.localeCompare(b.apellido));
                setEstudiantes(listaEst);
            } else {
                setEstudiantes([]);
            }

            // 2. Obtener asistencia general de la escuela para el turno seleccionado (curso_grupo = 0 o -1)
            const resAsis = await axios.post(URL_ASISTENCIA, {
                modo: 'obtenerAsistencia',
                fecha: fechaSeleccionada,
                curso: tipoTurno // 0 = Turno Habitual, -1 = Contraturno
            });

            if (resAsis.data && !resAsis.data.error) {
                setAsistencia(resAsis.data.asistencias || []);
                setAsistenciaAnual(resAsis.data.asistencias_anual || []);
                const fechasUnicas = [...new Set((resAsis.data.asistencias_anual || []).map(a => a.fecha))];
                setFechasConAsistencia(fechasUnicas);
            } else {
                setAsistencia([]);
                setAsistenciaAnual([]);
                setFechasConAsistencia([]);
            }
        } catch (error) {
            console.error("Error al cargar estudiantes y asistencia general:", error);
            setEstudiantes([]);
            setAsistencia([]);
        } finally {
            setLoadingEstudiantes(false);
        }
    };

    // Cambio en el selector / radio de asistencia por estudiante
    const handleSelectInasistenciaChange = (idUsuario, valor) => {
        setFormValuesAsistencia(prev => {
            const actual = prev[`${idUsuario}_`] || {};
            return {
                ...prev,
                [`${idUsuario}_`]: { ...actual, id_usuario: idUsuario, valor: valor }
            };
        });
    };

    // Alternar marca de Retiro para un estudiante (registro independiente)
    const handleToggleRetiro = (idUsuario, estadoRetiro) => {
        setFormValuesAsistencia(prev => {
            const actual = prev[`${idUsuario}_`] || {};
            return {
                ...prev,
                [`${idUsuario}_`]: { ...actual, id_usuario: idUsuario, retiro: estadoRetiro }
            };
        });
    };

    // Marcar todos como Presentes
    const marcarTodosComoPresentes = () => {
        const nuevosValores = { ...formValuesAsistencia };
        estudiantes.forEach(est => {
            const actual = nuevosValores[`${est.id}_`] || {};
            nuevosValores[`${est.id}_`] = {
                ...actual,
                id_usuario: est.id,
                valor: 'Presente'
            };
        });
        setFormValuesAsistencia(nuevosValores);
    };

    // Guardar Asistencia General (curso_grupo = 0 para Turno o -1 para Contraturno)
    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = Object.values(formValuesAsistencia);
        if (data.length === 0) {
            Swal.fire('Atención', 'No hay registros de asistencia modificados para guardar.', 'info');
            return;
        }

        setGuardando(true);
        try {
            const response = await axios.post(URL_ASISTENCIA, {
                modo: 'guardarAsistencia',
                fecha: fechaSeleccionada,
                curso: tipoTurno, // 0 = Turno Habitual, -1 = Contraturno
                responsable: loggeduserId,
                valores: data
            });

            if (!response.data.error) {
                Swal.fire({
                    icon: 'success',
                    title: 'Asistencia guardada',
                    text: `La asistencia general (${tipoTurno === -1 ? 'Contraturno' : 'Turno Habitual'}) ha sido registrada correctamente.`,
                    timer: 2000,
                    showConfirmButton: false
                });
                setFormValuesAsistencia({});
                cargarEstudiantesYAsistencia();
            } else {
                Swal.fire('Error', response.data.message || 'No se pudo guardar la asistencia.', 'error');
            }
        } catch (err) {
            console.error("Error al guardar asistencia:", err);
            Swal.fire('Error', 'Ocurrió un error al conectar con el servidor.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const nivelNombre = opcionesNiveles.find(n => String(n.id) === String(filtroNivel))?.nombre || '';

    return (
        <div className="container-principal">
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
                <h2>
                    <i className="fa-solid fa-clipboard-user text-primary me-2"></i>
                    Cargar Asistencia — Preceptoría
                </h2>
            </div>

            {/* Tarjeta de Filtros para selección de división (sin campo espacio/curso) */}
            <div className="card shadow mb-4 no-print">
                <div className="card-body">
                    <h5 className="card-title mb-3 fw-bold text-dark">
                        <i className="fa-solid fa-filter me-2 text-primary"></i>
                        Seleccionar Curso y División
                    </h5>
                    {loadingFiltros ? (
                        <div className="text-center py-3"><Espera /></div>
                    ) : (
                        <div className="row g-3">
                            <div className="col-12 col-md-2">
                                <label className="form-label small fw-bold">Ciclo Lectivo</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filtroCiclo} 
                                    onChange={e => {
                                        setFiltroCiclo(e.target.value);
                                        setFiltroNivel(''); setFiltroOrden(''); setFiltroSeccion('');
                                    }}
                                >
                                    <option value="">Seleccione Ciclo</option>
                                    {opcionesCiclos.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label small fw-bold">Nivel / Formación</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filtroNivel} 
                                    onChange={e => {
                                        setFiltroNivel(e.target.value);
                                        setFiltroOrden(''); setFiltroSeccion('');
                                    }}
                                >
                                    <option value="">Todos los niveles</option>
                                    {opcionesNiveles.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                </select>
                            </div>

                            <div className="col-12 col-md-2">
                                <label className="form-label small fw-bold">Año / Orden</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filtroOrden} 
                                    onChange={e => {
                                        setFiltroOrden(e.target.value);
                                        setFiltroSeccion('');
                                    }}
                                >
                                    <option value="">Todos los años</option>
                                    {opcionesOrdenes.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>

                            <div className="col-12 col-md-2">
                                <label className="form-label small fw-bold">División / Sección</label>
                                <select 
                                    className="form-select form-select-sm fw-bold" 
                                    value={filtroSeccion} 
                                    onChange={e => setFiltroSeccion(e.target.value)}
                                >
                                    <option value="">Todas las divisiones</option>
                                    {opcionesSecciones.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label small fw-bold text-primary">
                                    <i className="fa-solid fa-clock-rotate-left me-1"></i>
                                    Jornada / Turno General
                                </label>
                                <select 
                                    className="form-select form-select-sm fw-bold border-primary shadow-sm" 
                                    value={tipoTurno} 
                                    onChange={e => setTipoTurno(Number(e.target.value))}
                                >
                                    <option value={0}>Turno</option>
                                    <option value={-1}>Contraturno</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Panel de Estadísticas e Interacción de Asistencia */}
            {filtroCiclo && filtroNivel && filtroOrden && filtroSeccion && (
                <>
                    {/* Estadísticas de Asistencia General del día seleccionado */}
                    <div className="card shadow-sm mb-4 p-3 bg-light border-0">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold m-0 text-dark">
                                <i className="fa-solid fa-chart-pie me-2 text-success"></i>
                                Resumen Diario: {filtroOrden}° Div {filtroSeccion} ({nivelNombre}) — {fechaSeleccionada}
                            </h6>
                        </div>
                        <EstadisticaAsistencia asistencia={asistencia} fechaSeleccionada={fechaSeleccionada} />
                    </div>

                    {/* Barra de Control de Fecha y Calendario */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body p-3">
                            <div className="row align-items-center g-3">
                                <div className="col-12 col-md-5 d-flex align-items-center gap-2">
                                    <label className="fw-bold small text-nowrap mb-0">
                                        <i className="fa-regular fa-calendar me-1"></i>
                                        Fecha:
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={fechaSeleccionada}
                                        onChange={(e) => setFechaSeleccionada(e.target.value)}
                                        max={fechaHoy}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm text-nowrap"
                                        onClick={() => setVerCalendario(!verCalendario)}
                                    >
                                        <i className="fa-regular fa-calendar-days me-1"></i>
                                        {verCalendario ? 'Ocultar Calendario' : 'Ver asistencia cargada'}
                                    </button>
                                </div>

                                <div className="col-12 col-md-7 d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-success btn-sm px-3"
                                        onClick={marcarTodosComoPresentes}
                                        disabled={estudiantes.length === 0}
                                    >
                                        <i className="fa-solid fa-check-double me-1"></i>
                                        Marcar todos Presentes
                                    </button>
                                </div>
                            </div>

                            {/* Calendario de asistencias cargadas */}
                            {verCalendario && (
                                <div className="mt-3 pt-3 border-top text-center">
                                    <h6 className="fw-bold text-secondary mb-2">Días con asistencia registrada</h6>
                                    <CalendarioAsistencia 
                                        fechasConAsistencia={fechasConAsistencia}
                                        fechaSeleccionada={fechaSeleccionada}
                                        setFechaSeleccionada={setFechaSeleccionada}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tabla de Estudiantes para Carga de Asistencia */}
                    {loadingEstudiantes ? (
                        <div className="text-center py-5"><Espera /></div>
                    ) : estudiantes.length === 0 ? (
                        <div className="alert alert-info text-center py-4">
                            <i className="fa-solid fa-info-circle me-2 fs-5"></i>
                            No se encontraron estudiantes inscriptos en la división {filtroOrden}° {filtroSeccion}.
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="card shadow mb-4 overflow-hidden">
                                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h6 className="fw-bold m-0 text-dark">
                                        <i className="fa-solid fa-users me-2 text-primary"></i>
                                        Nómina de Estudiantes ({estudiantes.length}) — {filtroOrden}° División {filtroSeccion}
                                    </h6>
                                    <span className="badge bg-secondary-subtle text-dark border">
                                        Fecha: {fechaSeleccionada}
                                    </span>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-dark">
                                            <tr>
                                                <th style={{ width: '40%' }}>Estudiante</th>
                                                <th className="text-center" style={{ width: '40%' }}>Asistencia General</th>
                                                <th className="text-center" style={{ width: '20%' }}>
                                                    <i className="fa-solid fa-chart-pie me-1"></i>
                                                    Estadística Anual
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {estudiantes.map((est) => {
                                                const asisPrincipal = asistencia.find(a => String(a.id_usuario) === String(est.id) && a.fecha === fechaSeleccionada && ['Presente', 'Ausente', 'Tarde'].includes(a.asistencia));
                                                const asisRetiro = asistencia.find(a => String(a.id_usuario) === String(est.id) && a.fecha === fechaSeleccionada && a.asistencia === 'Retiro');

                                                const formVal = formValuesAsistencia[`${est.id}_`];
                                                const valorActual = (formVal && formVal.valor !== undefined) ? formVal.valor : (asisPrincipal?.asistencia || '');
                                                const retiroActual = (formVal && formVal.retiro !== undefined) ? formVal.retiro : Boolean(asisRetiro);

                                                return (
                                                    <tr key={est.id}>
                                                        <td>
                                                            <PerfilLogo usuario={est} version="extendida+" configuracion={configuracion} />
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
                                                                <div className="btn-group btn-group-sm" role="group">
                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`asistencia_${est.id}`}
                                                                        id={`p_${est.id}`}
                                                                        value="Presente"
                                                                        checked={valorActual === 'Presente'}
                                                                        onChange={() => handleSelectInasistenciaChange(est.id, 'Presente')}
                                                                    />
                                                                    <label className="btn btn-outline-success" htmlFor={`p_${est.id}`}>
                                                                        Presente
                                                                    </label>

                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`asistencia_${est.id}`}
                                                                        id={`a_${est.id}`}
                                                                        value="Ausente"
                                                                        checked={valorActual === 'Ausente'}
                                                                        onChange={() => handleSelectInasistenciaChange(est.id, 'Ausente')}
                                                                    />
                                                                    <label className="btn btn-outline-danger" htmlFor={`a_${est.id}`}>
                                                                        Ausente
                                                                    </label>

                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`asistencia_${est.id}`}
                                                                        id={`t_${est.id}`}
                                                                        value="Tarde"
                                                                        checked={valorActual === 'Tarde'}
                                                                        onChange={() => handleSelectInasistenciaChange(est.id, 'Tarde')}
                                                                    />
                                                                    <label className="btn btn-outline-warning text-dark" htmlFor={`t_${est.id}`}>
                                                                        Tarde
                                                                    </label>
                                                                </div>

                                                                {/* Botón independiente para marcar Retiro */}
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-sm ${retiroActual ? 'btn-warning text-dark fw-bold border-dark shadow-sm' : 'btn-outline-secondary'}`}
                                                                    onClick={() => handleToggleRetiro(est.id, !retiroActual)}
                                                                    title={retiroActual ? "Estudiante retirado (clic para quitar)" : "Marcar retiro del estudiante"}
                                                                >
                                                                    <i className="fa-solid fa-person-walking-arrow-right me-1"></i>
                                                                    Retiro
                                                                </button>

                                                                {(asisPrincipal || asisRetiro) && (
                                                                    <span className="text-success ms-1" title="Asistencia guardada previamente para esta fecha">
                                                                        <i className="fa-solid fa-circle-check fs-5"></i>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex justify-content-center">
                                                                <GraficoAsistencia asistenciaAnual={asistenciaAnual} id_usuario={est.id} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="card-footer bg-white text-center py-3">
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm"
                                        disabled={guardando}
                                    >
                                        {guardando ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Guardando asistencia...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-floppy-disk me-2"></i>
                                                Guardar Asistencia
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </>
            )}
        </div>
    );
}

export default CargarAsistencia;
